import * as angular from "angular";
import swal from "sweetalert2";
import { uniqBy } from "lodash";
import { FORM_ENTITY_CLASSIFICATION } from "../../../../../../../routes";
import { globs } from "../../../../../../../services/globs";
import { getAttributeClassificationsForType } from "../../../../../getAllowedClassifications";
import template from "./entity-classifications-editor.html";
import "./entity-classifications-editor.less";
import { ClassificationEditPlan } from "./classificationEditPlan";

export function controller(appState, loadingService, api, entityManager, toastr, securityService, $logger) {
	const ctrl = this;
	let classificationActions;

	ctrl.$onChanges = onChanges;
	ctrl.beginEditing = beginEditing;
	ctrl.leaveEditing = leaveEditing;
	ctrl.saveChanges = saveChanges;
	ctrl.save = saveChanges;
	ctrl.isDirty = isDirty;
	ctrl.toggleClassification = toggleClassification;
	ctrl.isClassificationSelected = isClassificationSelected;
	ctrl.canToggleClassification = canToggleClassification;
	ctrl.isPrimaryClassification = isPrimaryClassification;
	ctrl.hasEditableRows = hasEditableRows;
	ctrl.getClassificationToggleLabel = getClassificationToggleLabel;

	ctrl.$onInit = () => {
		ctrl.isLoading = false;
		ctrl.classificationTypes = appState.forms.selected.Agency.Classifications;
		classificationActions = globs.entityTypes
			.byType(ctrl.typeConfig.Code)
			.classifications;
		ctrl.currentClassifications = [];
		ctrl.addableClassifications = buildAddableClassifications();
		ctrl.editableClassifications = [];
		syncState();
	};

	function onChanges(changes) {
		const classificationsChange = changes.classifications;
		if(!classificationsChange || classificationsChange.isFirstChange()) {
			return;
		}

		syncState(classificationsChange.currentValue);
	}

	function beginEditing() {
		syncState();
		return hasEditableRows();
	}

	function leaveEditing() {
		ctrl.editPlan.reset();
		ctrl.editableClassifications = buildEditableClassifications();
		return true;
	}

	function isDirty() {
		return ctrl.editPlan.isDirty;
	}

	function hasEditableRows() {
		return ctrl.editableClassifications
			.some(classification => canToggleClassification(classification.Code));
	}

	function isClassificationSelected(code) {
		return ctrl.editPlan.isSelected(code);
	}

	function canToggleClassification(code) {
		if(isPrimaryClassification(code)) {
			return false;
		}

		const isOriginalSelection = ctrl.editPlan.wasSelected(code);
		const isCurrentSelection = ctrl.editPlan.isSelected(code);

		if(isCurrentSelection) {
			return isOriginalSelection
				? securityService.isActionAllowed(canRemoveAction(code))
				: true;
		}

		return isOriginalSelection
			? true
			: securityService.isActionAllowed(canAddAction(code));
	}

	function toggleClassification(code) {
		if(canToggleClassification(code)) {
			ctrl.editPlan.toggle(code);
		}
	}

	async function saveChanges() {
		const { toAdd, toRemove } = ctrl.editPlan.operationPlan;
		if(toAdd.length === 0 && toRemove.length === 0) {
			return true;
		}

		ctrl.isLoading = true;
		const savePromise = (async() => {
			for(const code of toRemove) {
				const route = FORM_ENTITY_CLASSIFICATION(appState.forms.selected.Id, ctrl.entity.Id, code);
				await api.delete(route);
			}

			for(const code of toAdd) {
				const route = FORM_ENTITY_CLASSIFICATION(appState.forms.selected.Id, ctrl.entity.Id, code);
				await postClassification(route);
			}
		})();

		try {
			await loadingService.watch(savePromise);
			const classifications = await reloadClassifications();
			syncState(classifications);
			toastr.success(`${ctrl.entity.Name ?? ctrl.entity.Code} classifications updated successfully.`);

			return true;
		}
		catch(err) {
			$logger.error("entity classifications update error", err);
			const classifications = await reloadClassifications();
			syncState(classifications);

			await swal.fire({
				title: "Classifications Reloaded",
				html:
					`<p>There was an error saving classifications.</p>
					<p>The latest classifications were reloaded from the server. Review the current values and try again.</p>`,
				showCloseButton: true,
				type: "warning"
			});

			return false;
		}
		finally {
			ctrl.isLoading = false;
		}
	}

	async function reloadClassifications() {
		return ctrl.onReload();
	}

	function syncState(classifications = ctrl.classifications) {
		ctrl.currentClassifications = classifications;
		ctrl.editPlan = createEditPlan(
			ctrl.currentClassifications.map(classification => classification.Code)
		);
		ctrl.editableClassifications = buildEditableClassifications();
	}

	function createEditPlan(assignedCodes) {
		const primaryCodes = ctrl.currentClassifications
			.filter(classification => classification.isPrimary)
			.map(classification => classification.Code);
		if(ctrl.entity.Classification?.Code) {
			primaryCodes.push(ctrl.entity.Classification.Code);
		}

		return new ClassificationEditPlan(assignedCodes, primaryCodes);
	}

	function buildAddableClassifications() {
		return getAttributeClassificationsForType(
			ctrl.typeConfig.Classifications,
			ctrl.classificationTypes
		)
			.filter(Boolean)
			.filter(classification => securityService.isActionAllowed(
				classificationActions.byCode(classification.Code).read
			))
			.filter(classification => securityService.isActionAllowed(canAddAction(classification.Code)));
	}

	function buildEditableClassifications() {
		const assignedClassifications = ctrl.currentClassifications
			.map(classification => ({ ...classification }));
		const selectableClassifications = ctrl.addableClassifications
			.map(classification => ({
				...entityManager.getClassification(classification.Code),
				...classification
			}));

		return sortClassifications(uniqBy([
			...assignedClassifications,
			...selectableClassifications
		], "Code"));
	}

	function sortClassifications(classifications) {
		return [...classifications].sort((left, right) => {
			if(!!left.isPrimary !== !!right.isPrimary) {
				return left.isPrimary ? -1 : 1;
			}

			return left.Code.localeCompare(right.Code);
		});
	}

	function isPrimaryClassification(code) {
		return ctrl.currentClassifications
			.some(classification => classification.Code === code && classification.isPrimary)
			|| ctrl.entity.Classification?.Code === code;
	}

	function canRemoveAction(code) {
		return classificationActions.byCode(code).remove;
	}

	function canAddAction(code) {
		return classificationActions.byCode(code).add;
	}

	function postClassification(route) {
		return ctrl.post
			? ctrl.post(route)
			: api.post(route);
	}

	function getClassificationToggleLabel(classification) {
		const action = isClassificationSelected(classification.Code) ? "Remove" : "Add";
		const name = classification.Name || classification.Code;

		return `${action} ${name} classification`;
	}
}

export default angular.module("vesl.components.entity-classifications-editor", [])
	.component("entityClassificationsEditor", {
		controller,
		template,
		bindings: {
			entity: "<",
			typeConfig: "<",
			classifications: "<",
			post: "=",
			onReload: "&"
		}
	})
	.name;
